/* eslint-disable max-classes-per-file */
import { ReactElement } from "react";
import { Descendant, Node } from "slate";

// eslint-disable-next-line import/prefer-default-export
export enum ElementType {
    HEADER = "HEADER",

    PARAGRAPH = "PARAGRAPH",
    BLOCKQUOTE = "BLOCKQUOTE",
    CODE_BLOCK = "CODE_BLOCK",
    LIST_ITEM = "LIST_ITEM",
    ORDERED_LIST = "ORDERED_LIST",
    UNORDERED_LIST = "UNORDERED_LIST",
    MENTION = "MENTION",
    IMAGE = "IMAGE"

}

export enum StyleType {
    BOLD = "BOLD",
    ITALIC = "ITALIC",
    STRIKETHROUGH = "STRIKETHROUGH",
    LINK = "LINK",
    NEW_LINE = "NEW_LINE",
    CODE = "CODE",
    MENTION = "MENTION",
    IMAGE = "IMAGE"
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ElementSettings {
    // empty
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BlockSettings {
    // empty
}

interface MentionSettings {
    avatarUrl?: string;
    name?: string;
    id?: string
}

export interface StateElement {
    type: ElementType;
    children: (TextElement | StateElement)[];
    settings?: MentionSettings;
}

export type TextElement = {
    [style in StyleType]: ElementSettings;
} & {
    text: string;
};

export type AnyChild = StateElement | TextElement | void | string | Descendant | Descendant[];

export interface EditorSettings {
    isReadOnly: boolean;
    onRemove: () => void;
    onUpdate: (element: StateElement) => void;
}

export interface BlockRenderer<T extends BlockSettings = BlockSettings> {
    toBlock: (child: ReactElement, settings: T, editorSettings: EditorSettings) => ReactElement;
    serialize: (child: string, settings: T | undefined) => string;
}

export interface BlockDeserializer<T> {
    nodeTypes: string[];
    deserialize: (children: T, child?: Element) => Descendant | Descendant[];
}

export interface ElementRenderer<T extends ElementSettings = ElementSettings> {
    toBlock: (child: ReactElement, settings: T, editorSettings: EditorSettings) => ReactElement;
    serialize: (child: string, settings: T) => string;
    apply?: (props: T) => T ;
}

export class Registry<TChild, T extends string, THandler extends BlockDeserializer<TChild> = BlockDeserializer<TChild>> {
    private handlers: Record<T, THandler> = Object.create(null);

    private nodeTypeHandlers: Record<string, BlockDeserializer<TChild>> = Object.create(null);

    public register(type: T, handler: THandler): void {
        this.handlers[type] = handler;
        handler.nodeTypes.forEach(node => {
            this.nodeTypeHandlers[node] = handler;
        });
    }

    public registerNodeHandler(handler: BlockDeserializer<TChild>): void {
        handler.nodeTypes.forEach(node => {
            this.nodeTypeHandlers[node] = handler;
        });
    }

    public get(type: T): THandler {
        return this.handlers[type];
    }

    public getByNodeType(type: string): BlockDeserializer<TChild> {
        return this.nodeTypeHandlers[type];
    }
}

export const BlockHandler = new Registry<AnyChild[], ElementType, BlockRenderer & BlockDeserializer<AnyChild[]>>();

export const InlineHandler = new Registry<TextElement, StyleType, ElementRenderer & BlockDeserializer<TextElement>>();
